import React, { useState } from 'react';
import { memo } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Edit, Trash2, Archive, RefreshCw, FileText, FolderArchive, AlertTriangle } from 'lucide-react';

// UI Components
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogClose, DialogDescription } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { TableCell, TableRow } from '../ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { MultiSelect } from '../ui/multi-select';
import { Combobox } from "../ui/combobox";

import { userHasAccessToDepartment, canUserEditProcedure, filterProceduresByArchivedStatus, canArchiveProcedure } from "../../utils/procedure-permissions";
import { getDepartmentIdByUser, toSydneyDate } from "../../utils/helpers";

// Types (ensure the path is correct for your project structure)
import { Procedure, User, Department, TrainingPlan } from '../../types';

import { ProcedureActions } from './ProcedureActions';
import { ACTIONS, RESOURCES } from "../../constants/permissions.constants";

// Props Interface
interface ProcedureTableRowProps {
  procedure: Procedure;
  canManageProcedures: boolean;
  showArchived: boolean;
  departments: Department[];
  users: User[];
  trainingPlans: TrainingPlan[];
  updateProcedure: (id: string, data: Partial<Procedure>, showToast: boolean) => void;
  archiveProcedure: (id: string, userId: string, reason: string) => void;
  unarchiveProcedure: (id: string, userId: string) => void;
  currentUser: User | null;
  isViewerOnly?: boolean;
  detailPathPrefix?: string;
}

// Utility functions that might be needed inside this component
const getUserId = (userOrId: string | User | null | undefined): string => {
  if (!userOrId) return "";
  return typeof userOrId === 'object' ? userOrId?.id : userOrId;
};

const getUserName = (userIdOrObject: string | User | null | undefined, users: User[]): string => {
  if (!userIdOrObject) return "Not assigned";

  if (typeof userIdOrObject === 'object' && userIdOrObject !== null && 'name' in userIdOrObject && userIdOrObject.name) {
    return userIdOrObject.name;
  }

  const userId = getUserId(userIdOrObject);
  const user = users.find(u => u.name === userId || u.id === userId); // Added u.name fallback just in case
  return user ? user.name : "Unknown User";
};

const getDepartmentInfo = (procDepartment: Department | null) => {
  return {
    name: procDepartment ? procDepartment.departmentName : "Unknown",
    id: procDepartment?.id || "",
    code: procDepartment?.departmentCode || ""
  };
};



const canDeleteProcedureCheck = (procedureId: string, trainingPlans: TrainingPlan[]) => {
  const hasTrainingPlanHistory = trainingPlans.some(plan => plan.procedures.filter(proc => proc.id == procedureId).length > 0);
  return { canDelete: !hasTrainingPlanHistory };
};


// The Memoized Row Component
export const ProcedureTableRow = memo(({
  procedure,
  canManageProcedures,
  showArchived,
  departments,
  users,
  trainingPlans,
  updateProcedure,
  archiveProcedure,
  unarchiveProcedure,
  currentUser,
  isViewerOnly = false,
  detailPathPrefix = '/procedure'
}: ProcedureTableRowProps) => {

  // --- STATE AND HANDLERS ARE NOW IN ProcedureActions ---


  const departmentInfo = getDepartmentInfo(procedure.department);
  const ownerName = getUserName(procedure.procedureOwner, users);

  const managerAndAboveRoles = ["Admin", "Director", "Senior Manager", "Manager"];
  const filteredUsers = users.filter(user => {
    // Only show users that are Manager level or above
    if (!managerAndAboveRoles.includes(user.userRole)) return false;

    const userDepartmentId = getDepartmentIdByUser(user);
    if (currentUser?.userRole === "Admin" || currentUser?.userRole === "Director" || currentUser?.jobRole === 'HSEQ Manager') {
      return true; // Show all manager-and-above users
    } else {
      return userDepartmentId == getDepartmentIdByUser(currentUser as User);
    }
  });

  const accessibleDepartments = departments.filter(
    dept => currentUser && userHasAccessToDepartment(currentUser, dept.departmentCode)
  );

  return (
    <TableRow>
      <TableCell>
        <Link to={`${detailPathPrefix}/${procedure.id}`} className="flex items-center gap-2 hover:underline">
          {procedure.archived ? <FolderArchive size={16} className="text-muted-foreground" /> : <FileText size={16} />}
          {procedure.procedureName}
        </Link>
      </TableCell>
      <TableCell>
        <Link to={`/departments/${departmentInfo.id}`} className="hover:underline">
          {departmentInfo.code} - {departmentInfo.name}
        </Link>
      </TableCell>
      <TableCell>
        <Link to={`/people/${getUserId(procedure.procedureOwner)}`} className="hover:underline">
          {ownerName}
        </Link>
      </TableCell>
      <TableCell>{procedure.procedureCadence}</TableCell>
      <TableCell>{toSydneyDate(procedure.createdAt)}</TableCell>
      {showArchived && <TableCell>{toSydneyDate(procedure.archivedOn)}</TableCell>}

      {!isViewerOnly && (
        <TableCell className="text-right">
          <ProcedureActions
            procedure={procedure}
            showArchived={showArchived}
            departments={departments}
            users={users}
            trainingPlans={trainingPlans}
            updateProcedure={updateProcedure}
            archiveProcedure={archiveProcedure}
            unarchiveProcedure={unarchiveProcedure}
            currentUser={currentUser}
            isViewerOnly={isViewerOnly}
            variant="outline"
          />
        </TableCell>
      )}
    </TableRow>
  );
});
